import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOperatorMode } from '../hooks/useOperatorMode';

const DISMISS_KEY = 'operator.install-coach.dismissed';
const CLOSE_ANIMATION_MS = 250;

export function InstallCoach() {
  const { isStandalone, isMobile } = useOperatorMode();
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (isStandalone) return;
    if (!isMobile) return;
    try {
      if (localStorage.getItem(DISMISS_KEY) === '1') return;
    } catch { /* */ }
    setVisible(true);
  }, [isStandalone, isMobile]);

  const dismiss = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      if (!mountedRef.current) return;
      setVisible(false);
      setClosing(false);
      try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* */ }
    }, CLOSE_ANIMATION_MS);
  }, []);

  useEffect(() => {
    if (!visible || closing) return;
    const dialog = dialogRef.current;
    if (!dialog) return;

    const focusable = dialog.querySelectorAll<HTMLElement>(
      'button, [href], input, [tabindex]:not([tabindex="-1"])',
    );
    focusable[0]?.focus();

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); dismiss(); return; }
      if (e.key === 'Tab' && focusable.length > 0) {
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first?.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [visible, closing, dismiss]);

  if (!visible) return null;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Install EmpowerID Operator"
      data-testid="install-coach"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 300,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        background: 'rgba(0, 0, 0, 0.6)',
        opacity: closing ? 0 : 1,
        transition: 'opacity 250ms ease',
      }}
    >
      <div
        style={{
          background: 'var(--card-bg, #1a1a2e)',
          borderRadius: '20px 20px 0 0',
          padding: '28px 24px',
          paddingBottom: `calc(28px + env(safe-area-inset-bottom))`,
          maxWidth: 420,
          width: '100%',
          margin: '0 auto',
          transform: closing ? 'translateY(100%)' : 'translateY(0)',
          transition: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1)',
          animation: closing ? undefined : 'operator-sheet-up 250ms cubic-bezier(0.4, 0, 0.2, 1) both',
        }}
      >
        <div aria-hidden="true" style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--text-secondary, rgba(255,255,255,0.3))', margin: '0 auto 24px' }} />

        <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary, #fff)', margin: '0 0 8px' }}>
          Install EmpowerID Operator
        </h2>
        <p style={{ fontSize: 14, color: 'var(--text-secondary, rgba(255,255,255,0.6))', margin: '0 0 24px', lineHeight: 1.5 }}>
          Add to your Home Screen for instant access, push notifications, and a native app experience.
        </p>

        {isIOS ? (
          <ol style={{ margin: 0, padding: '0 0 0 20px', listStyle: 'decimal', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              { icon: '📤', text: 'Tap the Share button in Safari' },
              { icon: '➕', text: 'Tap "Add to Home Screen"' },
              { icon: '🏠', text: 'Open Operator from your Home Screen' },
              { icon: '🔔', text: 'Enable notifications when prompted' },
            ].map((step, i) => (
              <li key={i} style={{ fontSize: 15, color: 'var(--text-primary, #fff)', lineHeight: 1.4 }}>
                <span style={{ marginRight: 8 }}>{step.icon}</span>
                {step.text}
              </li>
            ))}
          </ol>
        ) : (
          <p style={{ fontSize: 15, color: 'var(--text-primary, #fff)', lineHeight: 1.5, margin: 0 }}>
            Tap the browser menu and select "Install app" or "Add to Home Screen" to install.
          </p>
        )}

        <button
          onClick={dismiss}
          data-testid="install-coach-dismiss"
          style={{
            display: 'block',
            width: '100%',
            marginTop: 24,
            padding: '14px 0',
            borderRadius: 12,
            border: '1px solid var(--card-border, rgba(255,255,255,0.12))',
            background: 'none',
            color: 'var(--text-primary, #fff)',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
            minHeight: 48,
          }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}
